import { useState, useEffect } from "react";
import { ALL_WALLETS, connectWallet } from "@/lib/wallet";
import { ALL_TOKENS } from "@/data/tokens";
import { X, Loader2, ShieldCheck, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * In-flow wallet connection modal. Closes on success. Optional onSuccess() callback
 * fires after a wallet is connected — use it to resume the original action
 * (e.g., navigate to a staking page).
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   onSuccess  — (wallet) => void
 *   filterChain— "evm" | "solana" | null (null = all)
 *   reason     — optional one-line context shown in header
 */
export default function WalletModal({ open, onClose, onSuccess, filterChain = null, reason }) {
  const [busy, setBusy] = useState(null);
  const [stage, setStage] = useState("pick"); // pick | handshake | done

  useEffect(() => {
    if (!open) { setBusy(null); setStage("pick"); }
  }, [open]);

  if (!open) return null;

  const pick = async (id) => {
    const w = ALL_WALLETS.find(x => x.id === id);
    if (!w) return;
    setBusy(id);
    setStage("handshake");
    try {
      const wallet = await connectWallet(id, ALL_TOKENS);
      setStage("done");
      toast.success(`Connected via ${wallet.name}`);
      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess(wallet);
      }, 700);
    } catch {
      toast.error("Connection failed");
      setBusy(null);
      setStage("pick");
    }
  };

  const list = filterChain
    ? ALL_WALLETS.filter(w => w.chain === filterChain)
    : ALL_WALLETS;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center p-4 fade-in"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(14px)" }}
      onClick={onClose}
      data-testid="wallet-modal-backdrop"
    >
      <div
        className="w-full max-w-md border page-in relative overflow-hidden"
        style={{ background: "var(--surface-1)", borderColor: "var(--border-strong)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="wallet-modal"
      >
        {/* Top glow */}
        <div className="absolute -top-12 -left-12 w-40 h-40 rounded-full opacity-30 pointer-events-none"
             style={{ background: filterChain === "solana" ? "var(--sol-grad)" : "var(--evm-grad)" }} />

        <div className="relative flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--text-3)" }}>
              {stage === "pick" ? "Step 01 · Choose wallet" : stage === "handshake" ? "Step 02 · Handshake" : "Step 03 · Ready"}
            </div>
            <div className="font-display font-extrabold text-xl tracking-tighter">
              {stage === "pick" ? "Connect wallet" : stage === "handshake" ? "Approving…" : "Connected"}
            </div>
            {reason && stage === "pick" && (
              <div className="text-[11px] mt-1" style={{ color: "var(--text-2)" }}>{reason}</div>
            )}
          </div>
          <button onClick={onClose} data-testid="wallet-modal-close-btn" className="p-2 hover:bg-white/5"><X size={16} /></button>
        </div>

        {stage === "pick" && (
          <div className="relative px-5 py-4">
            {!filterChain && (
              <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-3)" }}>EVM Network</div>
            )}
            <div className="grid grid-cols-1 gap-2 mb-5">
              {list.filter(w => !filterChain || w.chain === "evm" || filterChain === "evm").filter(w => w.chain === "evm").map(w => (
                <WalletRow key={w.id} w={w} busy={busy === w.id} disabled={busy && busy !== w.id} onClick={() => pick(w.id)} />
              ))}
            </div>

            {(!filterChain || filterChain === "solana") && (
              <>
                <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-3)" }}>Solana Network</div>
                <div className="grid grid-cols-1 gap-2">
                  {list.filter(w => w.chain === "solana").map(w => (
                    <WalletRow key={w.id} w={w} busy={busy === w.id} disabled={busy && busy !== w.id} onClick={() => pick(w.id)} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {stage === "handshake" && (
          <div className="relative px-5 py-10 text-center">
            <div className="w-12 h-12 mx-auto mb-5 border-2 rounded-full" style={{ borderColor: "var(--border-strong)", borderTopColor: "#fff", animation: "spin 1s linear infinite" }} />
            <div className="text-sm" style={{ color: "var(--text-2)" }}>
              Approving connection in {ALL_WALLETS.find(w => w.id === busy)?.name}…
            </div>
            <div className="text-xs mt-2 font-mono" style={{ color: "var(--text-3)" }}>
              Waiting for signature
            </div>
          </div>
        )}

        {stage === "done" && (
          <div className="relative px-5 py-10 text-center">
            <div className="w-12 h-12 mx-auto mb-5 grid place-items-center rounded-full" style={{ background: "var(--success)", color: "#000" }}>
              <Check size={22} strokeWidth={3} />
            </div>
            <div className="font-display font-extrabold text-lg">Wallet linked</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>Resuming where you left off…</div>
          </div>
        )}

        <div className="relative px-5 py-4 border-t flex items-center gap-2 text-xs" style={{ borderColor: "var(--border)", color: "var(--text-3)" }}>
          <ShieldCheck size={14} />
          Self-custody. Stakeforge never sees your private keys.
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function WalletRow({ w, onClick, busy, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={`wallet-option-${w.id}`}
      className="flex items-center justify-between px-4 py-3 border lift text-left disabled:opacity-40 transition-all"
      style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 grid place-items-center rounded-full font-display font-black text-sm"
          style={{ background: w.color, color: "#fff" }}
        >{w.name[0]}</div>
        <div>
          <div className="font-medium text-sm">{w.name}</div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{w.chain}</div>
        </div>
      </div>
      {busy
        ? <Loader2 size={14} className="animate-spin" />
        : <ArrowRight size={14} style={{ color: "var(--text-3)" }} />}
    </button>
  );
}
